import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

import { HybridSlider } from 'src/components/hybrid-carousel/hybrid-carousel-slider';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import { Typography } from 'src/typography';
import { CategoriesTypeOption, RadiusOption, SpecialMenuCard } from 'src/gql/graphql';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';

import { categoryMatchesKey } from './match-category';
import { SpecialsCategoryCard } from './specials-category-card';

const SKELETON_CARD_COUNT = 6;

type SpecialsCategoryGridProps = {
  specials: SpecialMenuCard[];
  dispensaryCName: string;
  loading?: boolean;
};

export function SpecialsCategoryGrid({
  specials,
  dispensaryCName,
  loading = false,
}: SpecialsCategoryGridProps): JSX.Element | null {
  const { dispensaryRootUrl } = useUI();
  const hideSalesLanguage = useHideSalesLanguage();
  const { categories: dispensaryCategories } = useDispensaryCategories();
  const { dispensary } = useDispensary();
  const { type: categoryType, style: categoryStyle } = dispensary?.webCustomizationSettingsV2?.categories ?? {
    type: CategoriesTypeOption.large,
    style: RadiusOption.roundedRectangle,
  };

  const categoriesWithSpecials = useMemo(() => {
    const specialCategoryNames = new Set<string>();
    specials.forEach((special) => {
      (special.applicableCategories ?? []).forEach((ac) => {
        if (ac.category) {
          specialCategoryNames.add(ac.category);
        }
      });
    });

    const specialCategoryArray = [...specialCategoryNames];
    // Filter the admin-ordered dispensary categories to only those with applicable specials.
    // Preserves admin sort order and enriched category data (custom images, outlet card images/names).
    return dispensaryCategories.filter((cat) =>
      specialCategoryArray.some((papiName) => categoryMatchesKey(papiName, cat.key))
    );
  }, [specials, dispensaryCategories]);

  const heading = hideSalesLanguage ? 'Shop Offers By Category' : 'Shop Specials By Category';

  if (loading) {
    return (
      <Container data-testid='specials-category-grid-loading'>
        <SectionHeading size='medium' tag='h1'>
          {heading}
        </SectionHeading>
        <SkeletonRow role='status' aria-live='polite' aria-busy='true' aria-label='Loading categories'>
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_val, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonCard key={index} aria-hidden='true'>
              <SkeletonLabel />
              <SkeletonImage />
            </SkeletonCard>
          ))}
        </SkeletonRow>
      </Container>
    );
  }

  if (categoriesWithSpecials.length === 0) {
    return null;
  }

  return (
    <Container data-testid='specials-category-grid'>
      <SectionHeading size='medium' tag='h2' id='specials-category-grid-heading'>
        {heading}
      </SectionHeading>
      <HybridSlider carouselPadding={0}>
        {categoriesWithSpecials.map((category) => {
          const href = `/${dispensaryRootUrl}/${dispensaryCName}/specials/category/${category.key}`;
          return (
            <TileWrapper key={category.key} role='group' aria-roledescription='slide' aria-label={category.label}>
              <SpecialsCategoryCard category={category} href={href} type={categoryType} style={categoryStyle} />
            </TileWrapper>
          );
        })}
      </HybridSlider>
    </Container>
  );
}

const SectionHeading = styled(Typography.Heading)`
  margin: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px;
`;

// Each SpecialsCategoryCard variant (large/compact/stacked/text) sets its own explicit width,
// so the wrapper just hugs its content rather than forcing a fixed slide width.
const TileWrapper = styled.div`
  flex-shrink: 0;
  width: auto;
  margin-right: 12px;
`;

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  overflow: hidden;
  margin-top: 20px;
`;

const SkeletonCard = styled.div`
  flex-shrink: 0;
  width: 184px;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const SkeletonLabel = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 4px;
  height: 24px;
  width: 70%;
`;

const SkeletonImage = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  aspect-ratio: 640 / 340;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 8px;
  width: 100%;
`;
